import WisdomNavbar from "components/navbars/WisdomNabvar";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { createClassRoomEquipment, fetchClassRoomDetailsData, updateClassRoomEquipment } from "services/data";
import { fetchWisdomRelatedClassRooms, fetchWisdomRelatedSchools } from "services/wisdom/wisdomData";

const emptyNewTool = { name: "", location: "", year: "", working: 0, not_working: 0, reason: "" };

const WisdomAdminOrganizationWorkshopTools = () => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const isMultiSchoolAdmin = userInfo?.organization_id === 13;

    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [workshops, setWorkshops] = useState([]);
    const [selectedWorkshop, setSelectedWorkshop] = useState("");
    const [tools, setTools] = useState([]);

    const [loadingDetails, setLoadingDetails] = useState(false);
    const [savingId, setSavingId] = useState(null);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newTool, setNewTool] = useState(emptyNewTool);
    const [addingTool, setAddingTool] = useState(false);

    useEffect(() => {
        const loadClassRooms = async () => {
            const [schoolResponse, workshopResponse] = await Promise.all([
                fetchWisdomRelatedSchools(),
                fetchWisdomRelatedClassRooms(),
            ]);
            setSchools(schoolResponse);
            setWorkshops(workshopResponse);
        };

        loadClassRooms();
    }, []);

    const schoolId = isMultiSchoolAdmin ? selectedSchool : userInfo?.organization_id;

    const filteredWorkshops = useMemo(
        () =>
            workshops.filter((workshop) =>
                schoolId === "" || schoolId == null
                    ? true
                    : workshop?.organization_id === Number(schoolId)
            ),
        [workshops, schoolId]
    );

    useEffect(() => {
        if (!selectedWorkshop) {
            setTools([]);
            return;
        }

        const loadWorkshopTools = async () => {
            setLoadingDetails(true);
            try {
                const details = await fetchClassRoomDetailsData(selectedWorkshop);
                setTools(details?.details || []);
            } catch {
                toast.error(language ? "Failed to load workshop tools" : "فشل تحميل بيانات ادوات الورشة");
            } finally {
                setLoadingDetails(false);
            }
        };

        loadWorkshopTools();
        setShowAddForm(false);
        setNewTool(emptyNewTool);
    }, [selectedWorkshop]);

    const handleToolFieldChange = (id, field, value) => {
        setTools((prev) =>
            prev.map((tool) => (tool.id === id ? { ...tool, [field]: value } : tool))
        );
    };

    const handleSaveTool = async (tool) => {
        setSavingId(tool.id);
        try {
            await updateClassRoomEquipment(tool.id, {
                working: Number(tool.working) || 0,
                not_working: Number(tool.not_working) || 0,
                reason: tool.reason,
            });
            toast.success(language ? "Tool updated successfully" : "تم تحديث الأداة بنجاح");
        } catch {
            toast.error(language ? "Failed to update tool" : "فشل تحديث الأداة");
        } finally {
            setSavingId(null);
        }
    };

    const handleAddTool = async (e) => {
        e.preventDefault();
        if (!newTool.name.trim()) {
            toast.error(language ? "Tool name is required" : "اسم الأداة مطلوب");
            return;
        }

        setAddingTool(true);
        try {
            const created = await createClassRoomEquipment({
                classroom_id: selectedWorkshop,
                name: newTool.name,
                location: newTool.location,
                year: newTool.year || null,
                working: Number(newTool.working) || 0,
                not_working: Number(newTool.not_working) || 0,
                reason: newTool.reason,
            });
            setTools((prev) => [...prev, created]);
            setNewTool(emptyNewTool);
            setShowAddForm(false);
            toast.success(language ? "Tool added successfully" : "تمت إضافة الأداة بنجاح");
        } catch {
            toast.error(language ? "Failed to add tool" : "فشل إضافة الأداة");
        } finally {
            setAddingTool(false);
        }
    };

    return (
        <>
            <WisdomNavbar />
            <div className="max-w-3xl mx-auto px-4 py-8" dir={language ? "ltr" : "rtl"}>
                <div className="bg-white rounded-2xl shadow p-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                        {language ? "Workshop Tools" : "أدوات الورشة"}
                    </h1>

                    <div className="space-y-5 mb-6">
                        {isMultiSchoolAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language ? "School" : "مدرسة"}
                                </label>
                                <select
                                    value={selectedSchool}
                                    onChange={(e) => {
                                        setSelectedSchool(e.target.value);
                                        setSelectedWorkshop("");
                                    }}
                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                >
                                    <option value="">
                                        {language ? "All schools" : "كل المدارس"}
                                    </option>
                                    {schools.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language ? "Workshop" : "الورشة"}
                            </label>
                            <select
                                value={selectedWorkshop}
                                onChange={(e) => setSelectedWorkshop(e.target.value)}
                                className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                            >
                                <option value="">
                                    {language ? "Select workshop" : "اختر ورشة"}
                                </option>
                                {filteredWorkshops.map((workshop) => (
                                    <option key={workshop.id} value={workshop.id}>
                                        {workshop.Name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedWorkshop && (
                        loadingDetails ? (
                            <p className="text-sm text-gray-500">
                                {language ? "Loading workshop tools..." : "جاري تحميل بيانات ادوات الورشة..."}
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {tools.map((tool) => (
                                    <div
                                        key={tool.id}
                                        className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                    >
                                        <div className="flex items-center justify-between gap-3 mb-3">
                                            <h3 className="font-bold text-gray-900">{tool.name}</h3>
                                            <span className="text-xs text-gray-500">
                                                {[tool.location, tool.year].filter(Boolean).join(" - ")}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Working" : "العدد السليم"}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={tool.working ?? 0}
                                                    onChange={(e) =>
                                                        handleToolFieldChange(tool.id, "working", e.target.value)
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Not working" : "العدد غير السليم"}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={tool.not_working ?? 0}
                                                    onChange={(e) =>
                                                        handleToolFieldChange(tool.id, "not_working", e.target.value)
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Reason" : "السبب"}
                                                </label>
                                                <textarea
                                                    value={tool.reason ?? ""}
                                                    onChange={(e) =>
                                                        handleToolFieldChange(tool.id, "reason", e.target.value)
                                                    }
                                                    rows={2}
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => handleSaveTool(tool)}
                                            disabled={savingId === tool.id}
                                            className="py-2 px-4 rounded-xl bg-watomsBlue text-white hover:opacity-90 transition disabled:opacity-50"
                                        >
                                            {savingId === tool.id
                                                ? (language ? "Saving..." : "جاري الحفظ...")
                                                : (language ? "Save" : "حفظ")}
                                        </button>
                                    </div>
                                ))}

                                {tools.length === 0 && (
                                    <p className="text-sm text-gray-500">
                                        {language ? "No tools found for this workshop" : "لا توجد أدوات لهذه الورشة"}
                                    </p>
                                )}

                                {showAddForm ? (
                                    <form
                                        onSubmit={handleAddTool}
                                        className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 space-y-3"
                                    >
                                        <h4 className="font-bold text-gray-800">
                                            {language ? "New Tool" : "أداة جديدة"}
                                        </h4>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Name" : "الاسم"}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newTool.name}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, name: e.target.value }))
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Location" : "المكان"}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newTool.location}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, location: e.target.value }))
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Year" : "السنة"}
                                                </label>
                                                <input
                                                    type="number"
                                                    value={newTool.year}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, year: e.target.value }))
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Working" : "العدد السليم"}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={newTool.working}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, working: e.target.value }))
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Not working" : "العدد غير السليم"}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={newTool.not_working}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, not_working: e.target.value }))
                                                    }
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {language ? "Reason" : "السبب"}
                                                </label>
                                                <textarea
                                                    value={newTool.reason}
                                                    onChange={(e) =>
                                                        setNewTool((prev) => ({ ...prev, reason: e.target.value }))
                                                    }
                                                    rows={2}
                                                    className="w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                type="submit"
                                                disabled={addingTool}
                                                className="py-2 px-4 rounded-xl bg-watomsBlue text-white hover:opacity-90 transition disabled:opacity-50"
                                            >
                                                {addingTool
                                                    ? (language ? "Adding..." : "جاري الإضافة...")
                                                    : (language ? "Add Tool" : "إضافة الأداة")}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowAddForm(false);
                                                    setNewTool(emptyNewTool);
                                                }}
                                                className="py-2 px-4 rounded-xl bg-gray-200 text-gray-700 hover:opacity-90 transition"
                                            >
                                                {language ? "Cancel" : "إلغاء"}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setShowAddForm(true)}
                                        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                                    >
                                        <Plus size={18} />
                                        {language ? "Add New Tool" : "إضافة أداة جديدة"}
                                    </button>
                                )}
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    )
};

export default WisdomAdminOrganizationWorkshopTools;
